/* eslint-disable max-lines */
import {
    Button,
    Checkbox,
    pxToRem,
    TextInput,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';

import type { z } from 'zod';

import type { SubmitHandler } from 'react-hook-form';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { EyeLine, EyeOffLine } from '@archipro-design/icons';
import { useEnquirySession } from '~/modules/enquiry/hooks/use-enquiry';
import { useSearchParams } from 'react-router';
import { useAppFetcher } from '~/hooks/use-app-fetcher';
import type {
    LoginAction,
    SuccessResponseData,
} from '~/routes/remix-api.user.login';
import * as S from './LoginSignUpForm.style';

import { zodResolver } from '@hookform/resolvers/zod';
import {
    assertErrorResponse,
    assertSuccessResponse,
} from '@modules/root/graphql/responses';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import type { LoginFlow, signUpValidator } from '../../hook/use-login-flow';
import {
    hdeEmailDiscoveryValidator,
    hdeSignUpValidator,
    loginValidator,
    useLoginFlow,
} from '../../hook/use-login-flow';
import type { SignUpAction } from '~/routes/remix-api.user.sign-up';
import { useTracker } from '@archipro-website/tracker';
import { useUser } from '../../hook';
import ExpandableContainer from '../expandable-component/ExpandableContainer';
import type { AuthSource } from '../../type';
import { useAuth } from '~/modules/root/context';
import { trackAuthEvent } from '../../util/track-auth-event';
import { decodeLoginRedirect } from '~/modules/root';
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@rocco/components/form';
import { Button as RoccoButton } from '@rocco/components/button';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Input as RoccoInput } from '@rocco/components/input';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    getHdeFieldLabelClassName,
    getHdeInputClassName,
} from '~/modules/hde/components/registration/hde-option-styles';
import { AuthLoadingOverlay } from './AuthLoadingOverlay';
import {
    EmailCodeEntryStep,
    type EmailCodeStepChromeState,
} from './EmailCodeEntryStep';
import { useFeature } from '~/modules/root/hook/use-feature';
import { canShowEmailCodeLogin } from '../../util/login-policy';
import {
    GATE_FIELD_INPUT_CLASS,
    GATE_FIELD_LABEL_CLASS,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';

/** Auth sources where the modal was opened automatically or via login gating. */
const AUTO_PROMPTED_AUTH_SOURCES: ReadonlySet<AuthSource> = new Set([
    'siteGate',
    'enquiry',
    'designBoard',
    'downloadsgate',
    'articleGate',
    'contentGate',
    'productFiles',
    'specialtyPricingBanner',
    'tradeOnlyBanner',
]);

type FormShape = z.infer<typeof signUpValidator>;

/** Data collected in HDE mobile sign-up step 1, passed to step 2 for submission */
interface MobileStep1Data {
    email: string;
    password: string;
}

export interface LoginSignUpFormProps {
    flow: LoginFlow;
    onEmailBlur?: (email: string) => void;
    onResetPassword?: (e: React.SyntheticEvent<HTMLElement>) => void;
    defaultPublicUser?: PublicUser;
    defaultEmail?: string;
    emailLabel?: string;
    message?: string;
    allowExternalUrls?: boolean;
    skipPostSignUp?: boolean;
    markPostSignUpSeen?: boolean;
    skipAuthRedirect?: boolean;
    authSource: AuthSource;
    redirect?: string;
    onSuccess?: () => void;
    /**
     * When provided (redirect flows only), the post-auth redirect and
     * onSuccess are handed over as a `finish` closure instead of running
     * immediately, so the host can insert a step (Secure Sign-In enrolment)
     * between auth and navigation. Call `finish()` to complete the flow.
     */
    onAuthComplete?: (finish: () => void, meta: { isSignUp: boolean }) => void;
    /** Called when loading overlay is shown or hidden */
    onLoadingChange?: (loading: boolean) => void;
    variant?: 'default' | 'rocco' | 'hde';
    /** Called when Back is clicked (e.g. to return to social login view) */
    onBack?: () => void;
    /**
     * HDE mobile sign-up only: 1 = email+password step, 2 = full name+phone step.
     * When set, the form renders only the fields for that step.
     */
    signUpMobileStep?: 1 | 2;
    /** Called when step 1 is valid and user clicks Continue (HDE mobile sign-up only) */
    onMobileStep1Complete?: (data: MobileStep1Data) => void;
    /** Email and password from step 1, passed to step 2 for submission (HDE mobile sign-up only) */
    mobileStep1Data?: MobileStep1Data;
    /**
     * When false on HDE variant, submit is blocked in onSubmit (buttons stay enabled; use
     * onBlockedSubmitAttempt for feedback). Used for marketing consent gating on HDE homeowner flow.
     */
    isSubmissionAllowed?: boolean;
    /** Called when the user attempts submit while isSubmissionAllowed is false (HDE only). */
    onBlockedSubmitAttempt?: () => void;
    /** Rendered above the HDE footer row (e.g. marketing consent checkbox from parent). */
    hdeSubmissionConsentSlot?: React.ReactNode;
    /**
     * When true with variant hde, Back/Continue are not rendered; the parent modal footer submits
     * `#loginSignUpForm` via requestSubmit (e.g. HDE registration MultiStepFormContainer).
     */
    hideHdeFooterButtons?: boolean;
    /** Fires when login or sign-up Remix fetchers are busy (HDE modal footer loading state). */
    onAuthFetchersBusyChange?: (busy: boolean) => void;
    /** How the email-code trigger renders (rocco variant): inline link, or an OR divider + outlined pill button. */
    emailCodeStyle?: 'link' | 'button';
    /** Fires when the email-code entry step is shown or dismissed (passwordless step). */
    onEmailCodeStepChange?: (active: boolean) => void;
    /** When set with `onEmailCodeStepActiveChange`, parent controls the email-code step (e.g. modal header back). */
    emailCodeStepActive?: boolean;
    onEmailCodeStepActiveChange?: (active: boolean) => void;
    /** Email-code step send/load state for modal header chrome. */
    onEmailCodeStepChromeChange?: (state: EmailCodeStepChromeState) => void;
}

const LoginSignUpForm = (props: LoginSignUpFormProps) => {
    const [searchParams] = useSearchParams();
    const {
        flow,
        defaultPublicUser,
        defaultEmail,
        emailLabel,
        onEmailBlur,
        onResetPassword,
        allowExternalUrls = false,
        skipPostSignUp = false,
        markPostSignUpSeen = false,
        skipAuthRedirect = false,
        authSource,
        redirect = searchParams.has('redirect')
            ? decodeLoginRedirect(searchParams.get('redirect'))
            : undefined,
        onSuccess,
        onAuthComplete,
        onLoadingChange,
        variant = 'default',
        onBack,
        signUpMobileStep,
        onMobileStep1Complete,
        mobileStep1Data,
        isSubmissionAllowed = true,
        onBlockedSubmitAttempt,
        hdeSubmissionConsentSlot,
        hideHdeFooterButtons = false,
        onAuthFetchersBusyChange,
        emailCodeStyle = 'link',
        onEmailCodeStepChange,
        emailCodeStepActive: emailCodeStepActiveProp,
        onEmailCodeStepActiveChange,
        onEmailCodeStepChromeChange,
    } = props;

    const { css } = useStyles({ hasFlow: !!flow });
    const { mobile } = useAppDisplayModeContext();
    const user = useUser();
    const tracker = useTracker();
    const { authRedirect } = useAuth();

    const { enquirySession } = useEnquirySession();
    const [passwordVisible, setPasswordVisibility] = useState<boolean>(false);
    const [internalEmailCodeStepActive, setInternalEmailCodeStepActive] =
        useState(false);
    const emailCodeStepControlled = onEmailCodeStepActiveChange !== undefined;
    const emailCodeStepActive = emailCodeStepControlled
        ? (emailCodeStepActiveProp ?? false)
        : internalEmailCodeStepActive;
    const setEmailCodeStepActive = emailCodeStepControlled
        ? onEmailCodeStepActiveChange
        : setInternalEmailCodeStepActive;
    const emailCodeLoginEnabled = useFeature('email_code_login');

    useEffect(() => {
        onEmailCodeStepChange?.(emailCodeStepActive);
    }, [emailCodeStepActive, onEmailCodeStepChange]);
    const {
        submitButtonText,
        defaultFormValues,
        formValidator,
        displayFormSegments,
    } = useLoginFlow(flow);

    const {
        emailSegment,
        passwordSegment,
        firstNameSegment,
        lastNameSegment,
        rememberMeSegment,
    } = displayFormSegments;

    const baseDefaultValues = defaultFormValues(
        enquirySession ?? undefined,
        defaultPublicUser,
    );
    const defaultValues =
        defaultEmail !== undefined
            ? { ...baseDefaultValues, email: defaultEmail }
            : baseDefaultValues;

    const isHdeSignUp =
        variant === 'hde' &&
        (flow === 'sign-up' || flow === 'post-enquiry-sign-up');
    /** Homeowner: email field only until public-user lookup sets login vs sign-up. */
    const isHdeEmailDiscovery =
        variant === 'hde' && (flow === null || flow === undefined);
    const isMobileSignUpStep1 = signUpMobileStep === 1;
    const isMobileSignUpStep2 = signUpMobileStep === 2;
    const actualValidator = isMobileSignUpStep1
        ? loginValidator
        : isHdeSignUp
          ? hdeSignUpValidator
          : isHdeEmailDiscovery
            ? hdeEmailDiscoveryValidator
            : formValidator();
    const actualDefaultValues =
        isMobileSignUpStep2 && mobileStep1Data
            ? {
                  ...defaultValues,
                  email: mobileStep1Data.email,
                  password: mobileStep1Data.password,
                  phone: '',
              }
            : isHdeSignUp
              ? { ...defaultValues, phone: '' }
              : defaultValues;

    const loginFetcher = useAppFetcher<Record<string, unknown>>();
    const signupFetcher = useAppFetcher<Record<string, unknown>>();

    const methods = useForm<FormShape>({
        resolver: zodResolver(actualValidator),
        defaultValues: actualDefaultValues,
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });
    const { control, handleSubmit, setValue, trigger, watch } = methods;
    const watchedEmail = watch('email') ?? '';

    // Sync email/password from step 1 when on mobile sign-up step 2
    useEffect(() => {
        if (isMobileSignUpStep2 && mobileStep1Data) {
            setValue('email', mobileStep1Data.email);
            setValue('password', mobileStep1Data.password);
        }
    }, [isMobileSignUpStep2, mobileStep1Data, setValue]);

    /** Full name state used in HDE variant to combine first + last name into one field */
    const [fullName, setFullName] = useState(() => {
        const f = (defaultValues as { firstName?: string }).firstName ?? '';
        const l = (defaultValues as { lastName?: string }).lastName ?? '';
        return [f, l].filter(Boolean).join(' ').trim() || '';
    });

    const handleFullNameChange = (value: string) => {
        setFullName(value);
        const parts = value.trim().split(/\s+/).filter(Boolean);
        setValue('firstName', parts[0] ?? '');
        setValue('lastName', parts.slice(1).join(' '));
    };

    const loginErrorMessage =
        (loginFetcher.data &&
            assertErrorResponse(loginFetcher.data) &&
            loginFetcher.data.message) ||
        undefined;

    /**
     * Sign-up rejections (address refused, backend failure) are form-level rather than tied to a
     * single field, so they render as their own message instead of decorating an input.
     */
    const signUpErrorMessage =
        signupFetcher.data &&
        assertErrorResponse(signupFetcher.data) &&
        typeof signupFetcher.data.message === 'string'
            ? signupFetcher.data.message
            : undefined;

    const renderPasswordMessage = () => {
        return {
            dangerouslySetInnerHTML: {
                __html: loginErrorMessage,
            },
            ...(loginErrorMessage?.includes('reset your password') && {
                onClick: onResetPassword,
            }),
        };
    };

    const renderPasswordIcon = () => {
        if (passwordVisible) {
            return (
                <EyeOffLine
                    key="visible"
                    onClick={() => setPasswordVisibility(!passwordVisible)}
                />
            );
        }
        return (
            <EyeLine
                key="invisible"
                onClick={() => setPasswordVisibility(!passwordVisible)}
            />
        );
    };

    const successHandledRef = useRef(false);

    /**
     * Redirect-or-defer: when the host provides `onAuthComplete`, the
     * post-auth navigation (and the host's onSuccess) is handed over as a
     * `finish` closure so a step — Secure Sign-In enrolment — can render
     * between auth and the redirect. Without the prop, behaviour is unchanged.
     */
    const completeWithRedirect = useCallback(
        (performRedirect: () => void, isSignUp: boolean) => {
            const finish = () => {
                performRedirect();
                onSuccess?.();
            };
            if (onAuthComplete) {
                onAuthComplete(finish, { isSignUp });
                return;
            }
            finish();
        },
        [onAuthComplete, onSuccess],
    );

    const handleAuthSuccess = useCallback(() => {
        if (successHandledRef.current) return;

        if (signupFetcher.data && assertSuccessResponse(signupFetcher.data)) {
            successHandledRef.current = true;
            const signupGuestID = (
                signupFetcher.data.data as
                    | { trackedGuestID?: number }
                    | undefined
            )?.trackedGuestID;
            if (!skipAuthRedirect) {
                completeWithRedirect(
                    () =>
                        authRedirect({
                            authSource,
                            provider: 'EmailPassword',
                            authType: 'sign-up',
                            skipPostSignUp,
                            allowExternalUrls,
                            redirect,
                            // Read by the action that created the account, so a
                            // new sign-up is attributable to the guest activity
                            // that led to it — and the redirect need not wait
                            // on a revalidation to learn it.
                            guestID: signupGuestID,
                        }),
                    true,
                );
                return;
            }
            // The redirect path emits this via useAuthEvent on the
            // reloaded page; without a redirect nothing else will.
            void trackAuthEvent({
                tracker,
                event: 'new_account_registered',
                provider: 'EmailPassword',
                authSource,
                guestID: signupGuestID,
            });
            onSuccess?.();
        } else if (
            loginFetcher.data &&
            assertSuccessResponse<SuccessResponseData>(loginFetcher.data)
        ) {
            successHandledRef.current = true;
            if (!skipAuthRedirect) {
                const responseData = loginFetcher.data.data;
                const computedSkipPostSignUp =
                    skipPostSignUp || !responseData?.showPostSignUp;

                completeWithRedirect(
                    () =>
                        authRedirect({
                            authSource,
                            provider: 'EmailPassword',
                            authType: 'sign-in',
                            skipPostSignUp: computedSkipPostSignUp,
                            allowExternalUrls,
                            redirect,
                            // Reported by the action that authenticated them,
                            // so the redirect need not wait on a revalidation
                            // just to learn it.
                            guestID: responseData?.trackedGuestID,
                        }),
                    false,
                );
                return;
            }
            void trackAuthEvent({
                tracker,
                event: 'user_login',
                provider: 'EmailPassword',
                authSource,
            });
            onSuccess?.();
        }
    }, [
        signupFetcher.data,
        loginFetcher.data,
        skipAuthRedirect,
        authRedirect,
        authSource,
        skipPostSignUp,
        allowExternalUrls,
        redirect,
        onSuccess,
        tracker,
        completeWithRedirect,
    ]);

    /** Same post-success path as password sign-in; do not share successHandledRef (password flow may set it without closing). */
    const handleEmailCodeSuccess = useCallback(
        (trackedGuestID?: number) => {
            setEmailCodeStepActive(false);

            if (!skipAuthRedirect) {
                completeWithRedirect(
                    () =>
                        authRedirect({
                            authSource,
                            provider: 'EmailPassword',
                            authType: 'sign-in',
                            skipPostSignUp,
                            allowExternalUrls,
                            redirect,
                            guestID: trackedGuestID,
                        }),
                    false,
                );
                return;
            }
            // The redirect path emits this via useAuthEvent on the reloaded
            // page; without a redirect nothing else will.
            void trackAuthEvent({
                tracker,
                event: 'user_login',
                provider: 'EmailPassword',
                authSource,
                guestID: trackedGuestID,
            });
            onSuccess?.();
        },
        [
            allowExternalUrls,
            authRedirect,
            authSource,
            onSuccess,
            redirect,
            setEmailCodeStepActive,
            skipAuthRedirect,
            skipPostSignUp,
            tracker,
            completeWithRedirect,
        ],
    );

    const hasSignupSuccess =
        signupFetcher.data !== undefined &&
        assertSuccessResponse(signupFetcher.data);
    const hasLoginSuccessStrict =
        loginFetcher.data &&
        assertSuccessResponse<SuccessResponseData>(loginFetcher.data) &&
        user.__typename === 'Me';
    const hasLoginFetcherSuccess =
        loginFetcher.data &&
        assertSuccessResponse<SuccessResponseData>(loginFetcher.data);
    const hasAuthSuccess = hasSignupSuccess || hasLoginSuccessStrict;
    const hasFetcherSuccess = hasSignupSuccess || hasLoginFetcherSuccess;

    useEffect(() => {
        if (hasAuthSuccess && variant !== 'rocco') {
            handleAuthSuccess();
        }
    }, [hasAuthSuccess, variant, handleAuthSuccess]);

    // Auth failures render an inline message but previously logged nothing,
    // making "failed to authenticate, gave up" indistinguishable from
    // "closed the modal" in funnel analysis. One event per server response;
    // the message itself stays out of the payload.
    const authErrorLogged = useRef<{ login?: unknown; signup?: unknown }>({});
    useEffect(() => {
        if (
            loginFetcher.state === 'idle' &&
            loginFetcher.data &&
            assertErrorResponse(loginFetcher.data) &&
            authErrorLogged.current.login !== loginFetcher.data
        ) {
            authErrorLogged.current.login = loginFetcher.data;
            trackAuthEvent({
                tracker,
                event: 'AuthSignInError',
                authSource,
                provider: 'EmailPassword',
            });
        }
        if (
            signupFetcher.state === 'idle' &&
            signupFetcher.data &&
            assertErrorResponse(signupFetcher.data) &&
            authErrorLogged.current.signup !== signupFetcher.data
        ) {
            authErrorLogged.current.signup = signupFetcher.data;
            trackAuthEvent({
                tracker,
                event: 'AuthSignUpError',
                authSource,
                provider: 'EmailPassword',
            });
        }
    }, [
        loginFetcher.state,
        loginFetcher.data,
        signupFetcher.state,
        signupFetcher.data,
        tracker,
        authSource,
    ]);

    // Fallback: session can become Me before verify fetcher calls onSuccess.
    useEffect(() => {
        if (!emailCodeStepActive || user.__typename !== 'Me') {
            return;
        }
        handleEmailCodeSuccess();
    }, [emailCodeStepActive, handleEmailCodeSuccess, user.__typename]);

    useEffect(() => {
        if (variant !== 'hde' || !onAuthFetchersBusyChange) return;
        const busy =
            loginFetcher.state !== 'idle' || signupFetcher.state !== 'idle';
        onAuthFetchersBusyChange(busy);
    }, [
        variant,
        onAuthFetchersBusyChange,
        loginFetcher.state,
        signupFetcher.state,
    ]);

    /**
     * Addresses already counted as "entered" for this mount.
     *
     * The field reports on every blur and on every Enter, so a reader tabbing
     * back over it counted several entries for one address. Seeded with
     * `defaultEmail` because a host that runs its own email step ahead of this
     * form — the auth panel's step 0, on the gates that start there — has
     * already counted that address; without the seed the prefilled field's
     * first blur counted it a second time.
     *
     * Deliberately NOT seeded from the form's own prefill: the enquiry gates
     * fill the field from the saved enquiry session and never run step 0, so
     * that blur is the only report those journeys make. Suppressing it would
     * undercount them, which is the same measurement fault in the other
     * direction.
     */
    const emailInputReportedRef = useRef(
        new Set<string>(
            defaultEmail ? [defaultEmail.trim().toLowerCase()] : [],
        ),
    );

    const handleEmailBlur = useCallback(
        (email: string) => {
            const normalised = email.trim().toLowerCase();
            if (normalised && !emailInputReportedRef.current.has(normalised)) {
                emailInputReportedRef.current.add(normalised);
                trackAuthEvent({
                    tracker,
                    event: 'AuthEmailInput',
                    authSource,
                    provider: 'EmailPassword',
                });
            }
            onEmailBlur?.(email);
        },
        [tracker, authSource, onEmailBlur],
    );

    const onSubmit: SubmitHandler<FormShape> = (data) => {
        if (loginFetcher.state !== 'idle' || signupFetcher.state !== 'idle') {
            return;
        }

        /** Email-only step: Continue runs public-user lookup (login page + HDE homeowner). */
        if (!flow || isHdeEmailDiscovery) {
            if (isHdeEmailDiscovery && !isSubmissionAllowed) {
                onBlockedSubmitAttempt?.();
                return;
            }
            const email = (data.email ?? '').trim().toLowerCase();
            if (email) {
                void handleEmailBlur(email);
            }
            return;
        }

        if (variant === 'hde' && !isSubmissionAllowed) {
            onBlockedSubmitAttempt?.();
            return;
        }

        if (isMobileSignUpStep1 && onMobileStep1Complete) {
            onMobileStep1Complete({
                email: data.email ?? '',
                password: data.password ?? '',
            });
            return;
        }

        if (flow === 'login' || flow === 'post-enquiry-login') {
            tracker.log('accountLogin', {
                data: {
                    step: 2,
                    signInMethod: 'Email',
                },
                url: new URL(window.location.href),
            });

            trackAuthEvent({
                tracker,
                event: 'AuthSignInSubmit',
                authSource,
                provider: 'EmailPassword',
            });

            loginFetcher.submit(data, {
                method: 'post',
                action: `/remix-api/user/login`,
            });
        }

        if (flow === 'sign-up' || flow === 'post-enquiry-sign-up') {
            trackAuthEvent({
                tracker,
                event: 'AuthSignUpSubmit',
                authSource,
                provider: 'EmailPassword',
            });

            // Include markPostSignUpSeen flag for HDE to permanently skip post-sign-up
            signupFetcher.submit(
                { ...data, markPostSignUpSeen: markPostSignUpSeen.toString() },
                {
                    method: 'post',
                    action: `/remix-api/user/sign-up`,
                },
            );
        }
    };

    const isLoginFlow = flow === 'login' || flow === 'post-enquiry-login';
    const showEmailCodeOption =
        variant !== 'hde' &&
        canShowEmailCodeLogin({
            emailCodeLoginEnabled,
            flowIsLogin: isLoginFlow,
            email: watchedEmail,
            publicUserType: defaultPublicUser?.type,
            emailCodeLoginAvailable: defaultPublicUser?.emailCodeLoginAvailable,
        });
    const showForgotPassword =
        (variant === 'rocco' || variant === 'hde') &&
        isLoginFlow &&
        !!onResetPassword;

    // Only show overlay after auth succeeds (password verified by server).
    // Do not show during submission - button isLoading provides feedback until then.
    const showAuthOverlay =
        (variant === 'rocco' || variant === 'hde') && hasFetcherSuccess;
    useEffect(() => {
        if (variant === 'rocco' || variant === 'hde') {
            onLoadingChange?.(!!showAuthOverlay);
        }
    }, [variant, showAuthOverlay, onLoadingChange]);

    if (emailCodeStepActive && variant !== 'hde') {
        return (
            <EmailCodeEntryStep
                email={watchedEmail.trim().toLowerCase()}
                autoSend
                submitLabel={submitButtonText()}
                hideBack={variant !== 'rocco'}
                variant={variant === 'rocco' ? 'rocco' : 'legacy'}
                onChromeStateChange={onEmailCodeStepChromeChange}
                onBack={() => setEmailCodeStepActive(false)}
                onSuccess={handleEmailCodeSuccess}
            />
        );
    }

    if (variant === 'rocco' || variant === 'hde') {
        const showOverlay = showAuthOverlay;
        const isHde = variant === 'hde';
        /** Homeowner HDE: marketing opt-in once at the bottom of visible fields (before forgot password / footer). */
        const showHdeConsentAtFormBottom = isHde && !!hdeSubmissionConsentSlot;
        /* Spinner-only overlay — no status label (matches the revamp design). */
        const statusText = '';

        return (
            <FormProvider {...methods}>
                <div
                    className={cn(
                        !showOverlay && 'relative',
                        (showOverlay || isHde) &&
                            cn(
                                'flex min-h-0 flex-col',
                                isHde ? 'w-full' : 'flex-1',
                            ),
                    )}
                >
                    {showOverlay && (
                        <AuthLoadingOverlay
                            statusText={statusText}
                            isSuccess={!!hasFetcherSuccess}
                            onComplete={handleAuthSuccess}
                            dark={false}
                        />
                    )}
                    <loginFetcher.Form
                        id="loginSignUpForm"
                        onSubmit={handleSubmit(onSubmit)}
                        noValidate
                        className={cn(
                            'flex flex-col gap-2',
                            isHde ? 'w-full' : undefined,
                        )}
                    >
                        {emailSegment && !isMobileSignUpStep2 && (
                            <FormField
                                control={control}
                                name="email"
                                render={({ field, fieldState: { error } }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={
                                                isHde
                                                    ? getHdeFieldLabelClassName()
                                                    : GATE_FIELD_LABEL_CLASS
                                            }
                                        >
                                            {emailLabel ??
                                                (isHde
                                                    ? 'Your email'
                                                    : 'Email address')}
                                        </FormLabel>
                                        <FormControl>
                                            <RoccoInput
                                                {...field}
                                                type="email"
                                                placeholder="Enter email address"
                                                autoComplete={
                                                    isLoginFlow
                                                        ? 'username'
                                                        : 'email'
                                                }
                                                autoCapitalize="off"
                                                autoFocus={!mobile}
                                                color={
                                                    error ? 'error' : 'default'
                                                }
                                                className={
                                                    isHde
                                                        ? getHdeInputClassName()
                                                        : GATE_FIELD_INPUT_CLASS
                                                }
                                                onBlur={(e) => {
                                                    setValue(
                                                        'email',
                                                        e.target.value.toLowerCase(),
                                                    );
                                                    field.onBlur();
                                                    if (
                                                        e.relatedTarget?.id !==
                                                        'forgot-password-button'
                                                    ) {
                                                        handleEmailBlur(
                                                            field.value ?? '',
                                                        );
                                                    }
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleEmailBlur(
                                                            field.value ?? '',
                                                        );
                                                    }
                                                }}
                                            />
                                        </FormControl>
                                        <FormMessage
                                            className={
                                                isHde
                                                    ? 'text-red-600'
                                                    : undefined
                                            }
                                        />
                                    </FormItem>
                                )}
                            />
                        )}

                        {passwordSegment && !isMobileSignUpStep2 && (
                            <FormField
                                control={control}
                                name="password"
                                render={({ field, fieldState: { error } }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={
                                                isHde
                                                    ? getHdeFieldLabelClassName()
                                                    : GATE_FIELD_LABEL_CLASS
                                            }
                                        >
                                            Password
                                        </FormLabel>
                                        <FormControl>
                                            <div className="relative">
                                                <RoccoInput
                                                    {...field}
                                                    type={
                                                        passwordVisible
                                                            ? 'text'
                                                            : 'password'
                                                    }
                                                    placeholder="Enter password"
                                                    autoComplete={
                                                        isLoginFlow
                                                            ? 'current-password'
                                                            : 'new-password'
                                                    }
                                                    color={
                                                        loginErrorMessage ||
                                                        error
                                                            ? 'error'
                                                            : 'default'
                                                    }
                                                    className={cn(
                                                        'pr-10',
                                                        isHde
                                                            ? getHdeInputClassName()
                                                            : GATE_FIELD_INPUT_CLASS,
                                                    )}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setPasswordVisibility(
                                                            !passwordVisible,
                                                        )
                                                    }
                                                    className="absolute top-1/2 right-3 -translate-y-1/2 text-[#161616]/60"
                                                    aria-label={
                                                        passwordVisible
                                                            ? 'Hide password'
                                                            : 'Show password'
                                                    }
                                                >
                                                    {passwordVisible ? (
                                                        <EyeOff className="size-4" />
                                                    ) : (
                                                        <Eye className="size-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </FormControl>
                                        {error ? (
                                            <p
                                                role="alert"
                                                className="mt-2 text-sm text-destructive"
                                            >
                                                {error.message}
                                            </p>
                                        ) : loginErrorMessage ? (
                                            <p
                                                role="alert"
                                                className={cn(
                                                    'mt-2 text-sm text-destructive',
                                                    loginErrorMessage.includes(
                                                        'reset your password',
                                                    ) &&
                                                        'cursor-pointer underline',
                                                )}
                                                dangerouslySetInnerHTML={{
                                                    __html: loginErrorMessage,
                                                }}
                                                onClick={
                                                    loginErrorMessage.includes(
                                                        'reset your password',
                                                    )
                                                        ? onResetPassword
                                                        : undefined
                                                }
                                            />
                                        ) : (
                                            <FormMessage
                                                className={
                                                    isHde
                                                        ? 'text-red-600'
                                                        : undefined
                                                }
                                            />
                                        )}
                                    </FormItem>
                                )}
                            />
                        )}

                        {(firstNameSegment || lastNameSegment) &&
                            !isMobileSignUpStep1 && (
                                <FormItem>
                                    <FormLabel
                                        className={
                                            isHde
                                                ? getHdeFieldLabelClassName()
                                                : GATE_FIELD_LABEL_CLASS
                                        }
                                    >
                                        Full name
                                    </FormLabel>
                                    <FormControl>
                                        <RoccoInput
                                            value={fullName}
                                            onChange={(e) =>
                                                handleFullNameChange(
                                                    e.target.value,
                                                )
                                            }
                                            onBlur={() =>
                                                trigger([
                                                    'firstName',
                                                    'lastName',
                                                ])
                                            }
                                            // One field standing in for first +
                                            // last, so `name` rather than the
                                            // `given-name`/`family-name` pair
                                            // the enquiry form uses.
                                            autoComplete="name"
                                            placeholder="Enter your full name"
                                            className={
                                                isHde
                                                    ? getHdeInputClassName()
                                                    : GATE_FIELD_INPUT_CLASS
                                            }
                                        />
                                    </FormControl>
                                    <FormField
                                        control={control}
                                        name="firstName"
                                        render={({
                                            fieldState: {
                                                error: firstNameError,
                                            },
                                        }) => (
                                            <FormField
                                                control={control}
                                                name="lastName"
                                                render={({
                                                    fieldState: {
                                                        error: lastNameError,
                                                    },
                                                }) => {
                                                    const error =
                                                        firstNameError ||
                                                        lastNameError;
                                                    if (!error) return <></>;
                                                    return (
                                                        <FormMessage
                                                            className={cn(
                                                                isHde &&
                                                                    'text-red-600',
                                                            )}
                                                        >
                                                            {fullName.trim()
                                                                .length > 0
                                                                ? 'Please enter both first and last name'
                                                                : error.message}
                                                        </FormMessage>
                                                    );
                                                }}
                                            />
                                        )}
                                    />
                                </FormItem>
                            )}

                        {isHdeSignUp && !isMobileSignUpStep1 && (
                            <FormField
                                control={control}
                                name="phone"
                                render={({ field, fieldState: { error } }) => (
                                    <FormItem>
                                        <FormLabel
                                            className={getHdeFieldLabelClassName()}
                                        >
                                            Phone number (optional)
                                        </FormLabel>
                                        <FormControl>
                                            <RoccoInput
                                                {...field}
                                                placeholder="Enter your phone number"
                                                color={
                                                    error ? 'error' : 'default'
                                                }
                                                className={getHdeInputClassName()}
                                            />
                                        </FormControl>
                                        <FormMessage
                                            className={
                                                isHde
                                                    ? 'text-red-600'
                                                    : undefined
                                            }
                                        />
                                    </FormItem>
                                )}
                            />
                        )}

                        {signUpErrorMessage ? (
                            <p
                                role="alert"
                                className={cn(
                                    'mt-2 text-sm',
                                    isHde ? 'text-red-600' : 'text-red-500',
                                )}
                            >
                                {signUpErrorMessage}
                            </p>
                        ) : null}

                        {showHdeConsentAtFormBottom ? (
                            <div className="mt-4 w-full px-0 md:mt-6">
                                {hdeSubmissionConsentSlot}
                            </div>
                        ) : null}

                        {rememberMeSegment &&
                            !isMobileSignUpStep1 &&
                            !isMobileSignUpStep2 && (
                                <FormField
                                    control={control}
                                    name="rememberMe"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center gap-2 pt-2">
                                            <FormControl>
                                                <RoccoCheckbox
                                                    ref={field.ref}
                                                    checked={
                                                        field.value === 'true'
                                                    }
                                                    onCheckedChange={(
                                                        checked,
                                                    ) =>
                                                        setValue(
                                                            'rememberMe',
                                                            String(!!checked),
                                                        )
                                                    }
                                                />
                                            </FormControl>
                                            <FormLabel
                                                className={cn(
                                                    'mt-0! font-normal',
                                                    isHde && 'text-[#161616]',
                                                )}
                                            >
                                                Remember me
                                            </FormLabel>
                                        </FormItem>
                                    )}
                                />
                            )}

                        {isHde && showForgotPassword && (
                            <button
                                id="forgot-password-button"
                                type="button"
                                onClick={onResetPassword}
                                className="mt-2 text-center text-sm text-[#161616]/60 underline md:hover:text-[#161616]"
                            >
                                Forgot password?
                            </button>
                        )}

                        {isHde && !hideHdeFooterButtons ? (
                            <div className="mt-4 flex w-full flex-col gap-4 bg-[#EAEAEA] px-12 pb-10 md:mt-8 md:flex-row md:items-center md:justify-center md:gap-5 md:py-0">
                                <div className="flex justify-center gap-5 md:flex-row md:items-center">
                                    {onBack && (
                                        <RoccoButton
                                            type="button"
                                            onClick={onBack}
                                            size="larger"
                                            className="flex h-[2.875rem] w-33.75 items-center justify-center rounded-[var(--radius-btn-m)] border-0 bg-[#D4D4D4] font-medium text-[#161616] md:hover:bg-[#C4C4C4] md:hover:text-[#161616]"
                                        >
                                            Back
                                        </RoccoButton>
                                    )}
                                    <RoccoButton
                                        type="submit"
                                        size="larger"
                                        className="flex h-[2.875rem] w-33.75 items-center justify-center rounded-[var(--radius-btn-m)] border-0 bg-[#323232] font-medium text-white hover:bg-[#323232] hover:text-white md:hover:bg-[#4A4A4A]"
                                        isLoading={
                                            loginFetcher.state ===
                                                'submitting' ||
                                            signupFetcher.state === 'submitting'
                                        }
                                        disabled={
                                            user.__typename === 'Me' ||
                                            loginFetcher.state !== 'idle' ||
                                            signupFetcher.state !== 'idle'
                                        }
                                    >
                                        {submitButtonText()}
                                    </RoccoButton>
                                </div>
                            </div>
                        ) : null}
                        {!isHde ? (
                            <div className="mt-2 flex gap-3">
                                <RoccoButton
                                    type="submit"
                                    color="dark"
                                    layoutMode="fillFlex"
                                    size="larger"
                                    className="h-14 rounded-[var(--radius-btn-l)] text-[1.125rem] font-medium"
                                    isLoading={
                                        loginFetcher.state === 'submitting' ||
                                        signupFetcher.state === 'submitting'
                                    }
                                    disabled={
                                        user.__typename === 'Me' ||
                                        loginFetcher.state !== 'idle' ||
                                        signupFetcher.state !== 'idle'
                                    }
                                >
                                    {submitButtonText()}
                                </RoccoButton>
                            </div>
                        ) : null}

                        {!isHde && showForgotPassword && (
                            <button
                                id="forgot-password-button"
                                type="button"
                                onClick={onResetPassword}
                                className="mt-2 text-center text-sm text-gray-600 underline hover:text-gray-900"
                            >
                                Forgot password?
                            </button>
                        )}
                        {showEmailCodeOption &&
                            (emailCodeStyle === 'button' ? (
                                <>
                                    <div className="my-3 flex items-center justify-center gap-3 opacity-60">
                                        <span className="h-px flex-1 bg-[#9d9d9d]" />
                                        <span className="text-[0.9375rem] text-[#828282]">
                                            or
                                        </span>
                                        <span className="h-px flex-1 bg-[#9d9d9d]" />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setEmailCodeStepActive(true)
                                        }
                                        className="flex h-14 w-full items-center justify-center rounded-[var(--radius-btn-l)] border border-[#9d9d9d] text-[1.125rem] font-medium tracking-[-0.02em] text-[#656565] transition-colors hover:bg-black/5"
                                    >
                                        Email me a code
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setEmailCodeStepActive(true)}
                                    className="mt-2 text-center text-sm text-gray-600 underline hover:text-gray-900"
                                >
                                    Email me a code
                                </button>
                            ))}
                    </loginFetcher.Form>
                </div>
            </FormProvider>
        );
    }

    return (
        <loginFetcher.Form
            id="loginSignUpForm"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
        >
            <ExpandableContainer visible={emailSegment ?? false}>
                <Controller<FormShape>
                    control={control}
                    name={'email'}
                    render={({ field, fieldState: { error } }) => (
                        <TextInput
                            state={error?.message ? 'error' : 'default'}
                            className={css(S.EmailInput)}
                            message={error?.message}
                            size={mobile ? 'lg' : 'md'}
                            input={{
                                ...field,
                                placeholder: 'Enter email address',
                                autoComplete: 'email',
                                required: true,
                                autoFocus: !mobile,
                                autoCapitalize: 'off',
                                onBlur: async (
                                    e: React.FocusEvent<HTMLInputElement>,
                                ) => {
                                    setValue(
                                        'email',
                                        e.target.value.toLowerCase(),
                                    );
                                    field.onBlur?.();
                                    if (
                                        e.relatedTarget?.id ===
                                        'forgot-password-button'
                                    ) {
                                        return;
                                    }

                                    handleEmailBlur(field.value ?? '');
                                },
                                onKeyDown: (e: React.KeyboardEvent) => {
                                    if (e.key === 'Enter') {
                                        handleEmailBlur(field.value ?? '');
                                    }
                                },
                                ...(!mobile && {
                                    label: 'Email address',
                                }),
                            }}
                        />
                    )}
                />
            </ExpandableContainer>

            <ExpandableContainer visible={passwordSegment ?? false}>
                <Controller<FormShape>
                    control={control}
                    name={'password'}
                    render={({ field, fieldState: { error } }) => (
                        <TextInput
                            className={css(S.PasswordInput)}
                            state={
                                error?.message || loginErrorMessage
                                    ? 'error'
                                    : 'default'
                            }
                            message={
                                error?.message
                                    ? error.message
                                    : loginErrorMessage
                                      ? renderPasswordMessage()
                                      : undefined
                            }
                            size="md"
                            input={{
                                ...field,
                                placeholder: 'Enter password',
                                autoComplete: 'current-password',
                                required: true,
                                input: {
                                    type: passwordVisible ? 'text' : 'password',
                                },
                                ...(!mobile && {
                                    label: 'Password',
                                }),
                            }}
                            suffixIcons={[renderPasswordIcon()]}
                        />
                    )}
                />
            </ExpandableContainer>

            <ExpandableContainer
                visible={(firstNameSegment || lastNameSegment) ?? false}
            >
                <Controller<FormShape>
                    control={control}
                    name={'firstName'}
                    render={({ field, fieldState: { error } }) => (
                        <TextInput
                            state={error?.message ? 'error' : 'default'}
                            message={error?.message}
                            size="md"
                            input={{
                                ...field,
                                placeholder: 'Enter first name',
                                required: true,
                                ...(!mobile && {
                                    label: 'First name',
                                }),
                            }}
                        />
                    )}
                />
                <Controller<FormShape>
                    control={control}
                    name={'lastName'}
                    render={({ field, fieldState: { error } }) => (
                        <TextInput
                            state={error?.message ? 'error' : 'default'}
                            message={error?.message}
                            size="md"
                            input={{
                                ...field,
                                placeholder: 'Enter last name',
                                required: true,
                                ...(!mobile && {
                                    label: 'Last name',
                                }),
                            }}
                        />
                    )}
                />
            </ExpandableContainer>

            {rememberMeSegment && (
                <Controller<FormShape>
                    control={control}
                    name={'rememberMe'}
                    render={({ field }) => (
                        <Checkbox
                            className={css(S.RememberMeInput)}
                            {...field}
                            label={{
                                children: `Remember me`,
                            }}
                            defaultChecked={true}
                            onChange={(_, data) => {
                                const val = !!data?.checked;
                                setValue('rememberMe', val.toString());
                            }}
                            variables={{
                                checkboxIndicatorLabelGap: pxToRem(4),
                                checkboxIndicatorLabelCheckedGap: pxToRem(4),
                                labelLineHeight: pxToRem(18),
                            }}
                        />
                    )}
                />
            )}

            {showEmailCodeOption && (
                <button
                    type="button"
                    onClick={() => setEmailCodeStepActive(true)}
                    className={css(S.EmailCodeLink)}
                >
                    Email me a code
                </button>
            )}

            {signUpErrorMessage ? (
                <p role="alert" className="mt-2 text-sm text-red-500">
                    {signUpErrorMessage}
                </p>
            ) : null}

            <Button
                design={{
                    marginTop: pxToRem(44),
                }}
                style={{
                    animation: AUTO_PROMPTED_AUTH_SOURCES.has(authSource)
                        ? 'loginContinueAttention 0.8s ease-out 0.5s both'
                        : undefined,
                }}
                fluid
                size={18}
                key={'login-button'}
                type={'submit'}
                color={'dark'}
                loading={
                    loginFetcher.state === 'submitting' ||
                    signupFetcher.state === 'submitting'
                }
                disabled={
                    user.__typename === 'Me' ||
                    loginFetcher.state !== 'idle' ||
                    signupFetcher.state !== 'idle'
                }
                variables={{ iconPadding: 0 }}
            >
                {submitButtonText()}
            </Button>
        </loginFetcher.Form>
    );
};

export default LoginSignUpForm;
